import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaHeart,
  FaComments,
  FaUser,
  FaCrown,
  FaUserPlus,
  FaCheck,
  FaClock,
  FaCoins,
  FaUserFriends,
  FaEdit,
  FaSave,
  FaCamera,
  FaImage,
  FaGift,
  FaShareAlt,
  FaCopy,
} from 'react-icons/fa';
import ActorAvatar from './ActorAvatar.jsx';
import EcoscicleBadgeDisplay from './EcoscicleBadgeDisplay.jsx';
import AffiliationsSection from './AffiliationsSection.jsx';
import ImageCropModal from './ImageCropModal.jsx';
import PeriodSelector from './PeriodSelector.jsx';
import { useProfileSidebar } from '../contexts/ProfileSidebarContext.jsx';
import { toast } from './Toast.jsx';
import { uploadProfileImage } from '../utils/imageUploadAdapter.js';
import axios from 'axios';

const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const LOCALHOST_API = 'http://localhost:3000';
  
  const isLocalhost = 
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  
  return isLocalhost ? LOCALHOST_API : RAILWAY_API;
};
const API_URL = getApiUrl();

const ProfileSidebar = ({ onTrust, onChat, currentUser }) => {
  const { isOpen, profileData: profile, closeProfile, loading: profileLoading, openProfile } = useProfileSidebar();
  const [localProfile, setLocalProfile] = useState(null);
  
  useEffect(() => {
    setLocalProfile(profile);
    if (profile) {
      console.log('ProfileSidebar profile:', profile);
      console.log('ProfileSidebar profile.bio:', profile.bio);
    }
  }, [profile]);
  const [friendshipStatus, setFriendshipStatus] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditAnimating, setIsEditAnimating] = useState(false);
  const [editForm, setEditForm] = useState({
    display_name: '',
    bio: '',
  });
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [bannerImageFile, setBannerImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [bannerImagePreview, setBannerImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropImageType, setCropImageType] = useState(null); // 'profile' or 'banner'
  
  // 収支一覧関連のstate
  const [finances, setFinances] = useState([]);
  const [financeSummary, setFinanceSummary] = useState({
    total_income: 0,
    total_expense: 0,
    net_balance: 0,
  });
  const [financePeriod, setFinancePeriod] = useState('30days');
  const [financesExpanded, setFinancesExpanded] = useState(true);

  const [inviteLink, setInviteLink] = useState('');
  const [inviteCopySuccess, setInviteCopySuccess] = useState(false);

  const generateInviteLink = async () => {
    const currentActorId = (localProfile || profile)?.actor_id || (localProfile || profile)?.actorId;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/special-rewards/generate-invite-link`,
        {
          actor_id: parseInt(currentActorId),
          custom_message: '真決DAOで共に自律評価経済を実践しましょう！',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setInviteLink(response.data.invite_url);
      }
    } catch (error) {
      console.error('Invite link generation error:', error);
    }
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteCopySuccess(true);
      setTimeout(() => setInviteCopySuccess(false), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setShouldRender(false);
      setIsEditMode(false);
    }
  };

  // 編集フォーム初期化
  useEffect(() => {
    const currentProfile = localProfile || profile;
    if (currentProfile && isOpen) {
      setEditForm({
        display_name: currentProfile.display_name || currentProfile.displayName || '',
        bio: currentProfile.bio || currentProfile.description || '',
      });
    }
  }, [localProfile, profile, isOpen]);

  // 編集モードのアニメーション制御
  useEffect(() => {
    if (isEditMode) {
      setTimeout(() => setIsEditAnimating(true), 10);
    } else {
      setIsEditAnimating(false);
    }
  }, [isEditMode]);

  // プロフィールが開かれたら収支データを取得
  useEffect(() => {
    const loadFinances = async (actorId, period) => {
      try {
        const token = localStorage.getItem('token');
        if (!token || !actorId) return;

        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await fetch(`${API_URL}/api/actors/${actorId}/finances?period=${period}`, config);

        if (response.ok) {
          const data = await response.json();
          setFinances(data.finances || []);
          setFinanceSummary(data.summary || { total_income: 0, total_expense: 0, net_balance: 0 });
        }
      } catch (error) {
        console.error('収支の取得エラー:', error);
      }
    };

    const currentProfile = localProfile || profile;
    const profileActorId = currentProfile?.actor_id || currentProfile?.id;

    if (isOpen && profileActorId && financesExpanded) {
      loadFinances(profileActorId, financePeriod);
    }
  }, [isOpen, localProfile, profile, financePeriod, financesExpanded]);

  // 自分のプロフィールかどうか
  const currentProfile = localProfile || profile;
  const profileActorId = currentProfile?.actor_id || currentProfile?.actorId;
  const isOwnProfile = currentUser && currentProfile && (
    currentUser.actorId === currentProfile.actor_id ||
    currentUser.actorId === currentProfile.actorId ||
    currentUser.actor_id === currentProfile.actor_id ||
    currentUser.actor_id === currentProfile.actorId
  );

  // 画像選択時の処理（クロップモーダルを開く）
  const handleImageSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setCropImageType(type);
    };
    reader.readAsDataURL(file);
    
    // input をリセット（同じファイルを再選択可能にする）
    e.target.value = '';
  };

  // クロップ完了時の処理
  const handleCropComplete = (croppedBlob) => {
    const previewUrl = window.URL.createObjectURL(croppedBlob);
    
    if (cropImageType === 'profile') {
      setProfileImageFile(croppedBlob);
      setProfileImagePreview(previewUrl);
    } else if (cropImageType === 'banner') {
      setBannerImageFile(croppedBlob);
      setBannerImagePreview(previewUrl);
    }
    setCropImageSrc(null);
    setCropImageType(null);
  };

  // クロップキャンセル時の処理
  const handleCropCancel = () => {
    setCropImageSrc(null);
    setCropImageType(null);
  };

  useEffect(() => {
    const checkFriendshipStatus = async () => {
      if (!currentUser?.id || !profile?.id || !isOpen) return;

      try {
        const response = await fetch(
          `${API_URL}/api/friendship/status/${profile.id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setFriendshipStatus(data.status);
        } else if (response.status === 404) {
          setFriendshipStatus(null);
        }
      } catch (error) {
        setFriendshipStatus(null);
      }
    };

    const checkFollowStatus = async () => {
      if (!currentUser?.id || !profile?.id || !isOpen) return;

      try {
        const profileActorId = profile.actor_id || profile.id;
        const response = await fetch(
          `${API_URL}/api/follow/status/${profileActorId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setIsFollowing(data.isFollowing || false);
        } else {
          setIsFollowing(false);
        }
      } catch (error) {
        setIsFollowing(false);
      }
    };

    if (isOpen && profile?.id) {
      checkFriendshipStatus();
      checkFollowStatus();
    }
  }, [isOpen, currentUser?.id, profile?.id]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeProfile();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeProfile]);

  if (!shouldRender) return null;

  const actorType = profile?.actorType || profile?.actor_type || profile?.type || 'user';
  
  // Actorのdisplay_nameを表示（プロフィール画面で編集した名前）
  const getDisplayName = () => {
    // displayName（キャメルケース）またはdisplay_name（スネークケース）を使用
    const name = profile?.displayName || profile?.display_name || '';
    if (!name) return '匿名ユーザー';
    return name;
  };

  const handleFriendRequest = async () => {
    const profileActorId = profile?.actor_id || profile?.id;
    const currentActorId = currentUser?.actor?.actor_id || currentUser?.actorId || currentUser?.id;
    
    if (!currentActorId || !profileActorId) {
      toast.error('アクター情報が見つかりません');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/friendship/request`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          receiver_actor_id: profileActorId,
          relationship_tag: 'friend',
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setFriendshipStatus('pending_sent');
        toast.success('友達申請を送信しました');
      } else {
        toast.error(data.error || '友達申請に失敗しました');
      }
    } catch (error) {
      toast.error('友達申請中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptFriend = async () => {
    const profileActorId = profile?.actor_id || profile?.id;
    if (!currentUser?.actor?.actor_id || !profileActorId) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/friendship/accept/${profileActorId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        setFriendshipStatus('accepted');
        toast.success('友達申請を承認しました');
      } else {
        const data = await response.json();
        toast.error(data.error || '友達申請の承認に失敗しました');
      }
    } catch (error) {
      console.error('友達申請承認エラー:', error);
      toast.error('友達申請承認中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectFriend = async () => {
    const profileActorId = profile?.actor_id || profile?.id;
    if (!currentUser?.actor?.actor_id || !profileActorId) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/friendship/reject/${profileActorId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        setFriendshipStatus(null);
        toast.success('友達申請を拒否しました');
      } else {
        const data = await response.json();
        toast.error(data.error || '友達申請の拒否に失敗しました');
      }
    } catch (error) {
      console.error('友達申請拒否エラー:', error);
      toast.error('友達申請拒否中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelFriendRequest = async () => {
    const profileActorId = profile?.actor_id || profile?.id;
    if (!currentUser?.actor?.actor_id || !profileActorId) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/friendship/cancel/${profileActorId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        setFriendshipStatus(null);
        toast.success('友達申請を取り消しました');
      } else {
        const data = await response.json();
        toast.error(data.error || '友達申請の取り消しに失敗しました');
      }
    } catch (error) {
      console.error('友達申請取り消しエラー:', error);
      toast.error('友達申請取り消し中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFollow = async () => {
    const profileActorId = profile?.actor_id || profile?.id;
    if (!currentUser?.actor?.actor_id || !profileActorId) return;

    setLoading(true);
    try {
      if (isFollowing) {
        const response = await fetch(
          `${API_URL}/api/follow/${profileActorId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          setIsFollowing(false);
          toast.success('フォローを解除しました');
        } else {
          const data = await response.json();
          toast.error(data.error || 'フォロー解除に失敗しました');
        }
      } else {
        const response = await fetch(`${API_URL}/api/follow`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            following_actor_id: profileActorId,
          }),
        });

        if (response.ok) {
          setIsFollowing(true);
          toast.success('フォローしました');
        } else {
          const data = await response.json();
          toast.error(data.error || 'フォローに失敗しました');
        }
      }
    } catch (error) {
      console.error('フォロー処理エラー:', error);
      toast.error('フォロー処理中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // 収支データを取得
  const fetchFinances = async (actorId, period = financePeriod) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !actorId) return;

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await fetch(`${API_URL}/api/actors/${actorId}/finances?period=${period}`, config);
      
      if (response.ok) {
        const data = await response.json();
        setFinances(data.finances || []);
        setFinanceSummary(data.summary || { total_income: 0, total_expense: 0, net_balance: 0 });
      }
    } catch (error) {
      console.error('収支の取得エラー:', error);
    }
  };

  // 保存処理（Profile.jsxから移植）
  const handleSaveProfile = async () => {
    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const actorId = userData.actor?.id || userData.actor?.actor_id || userData.actorId || userData.actor_id;

      if (!actorId) {
        throw new Error('Actor IDが見つかりません');
      }

      let updatedProfileImageUrl = null;
      let updatedProfilePublicId = null;
      let updatedBannerImageUrl = null;
      let updatedBannerPublicId = null;

      if (profileImageFile) {
        const { url, publicId } = await uploadProfileImage(profileImageFile);
        updatedProfileImageUrl = url;
        updatedProfilePublicId = publicId;
      }

      if (bannerImageFile) {
        const isRailway = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
        if (isRailway) {
          const { uploadToCloudinary } = await import('../utils/cloudinaryUpload.js');
          const result = await uploadToCloudinary(
            new window.File([bannerImageFile], 'banner.jpg', { type: 'image/jpeg' }),
            'banners'
          );
          updatedBannerImageUrl = result.secure_url;
          updatedBannerPublicId = result.public_id;
        } else {
          const formData = new FormData();
          formData.append('profile_image', bannerImageFile, 'banner.jpg');

          const response = await fetch(`${API_URL}/api/actor-profile/${actorId}/upload-image?type=banner`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          });

          if (!response.ok) {
            throw new Error('バナー画像のアップロードに失敗しました');
          }
          
          const data = await response.json();
          updatedBannerImageUrl = data.banner_image_url;
        }
      }

      const profileData = {
        display_name: editForm.display_name,
        description: editForm.bio,
      };

      if (updatedProfileImageUrl) {
        profileData.profile_image_url = updatedProfileImageUrl;
        profileData.profile_image_public_id = updatedProfilePublicId;
      }

      if (updatedBannerImageUrl) {
        profileData.banner_image_url = updatedBannerImageUrl;
        profileData.banner_image_public_id = updatedBannerPublicId;
      }

      await axios.put(
        `${API_URL}/api/actor-profile/${actorId}`,
        profileData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // localStorageを更新
      const updatedUserData = { ...userData };
      let userDataUpdated = false;
      
      if (updatedProfileImageUrl) {
        updatedUserData.iconUrl = updatedProfileImageUrl;
        updatedUserData.profile_image_url = updatedProfileImageUrl;
        if (updatedUserData.actor) {
          updatedUserData.actor.profile_image_url = updatedProfileImageUrl;
        }
        userDataUpdated = true;
      }
      
      if (updatedBannerImageUrl) {
        if (updatedUserData.actor) {
          updatedUserData.actor.banner_image_url = updatedBannerImageUrl;
        }
        userDataUpdated = true;
      }
      
      if (userDataUpdated) {
        localStorage.setItem('user', JSON.stringify(updatedUserData));
        window.postMessage({ type: 'userDataUpdated', detail: updatedUserData }, window.location.origin);
      }

      setProfileImageFile(null);
      setBannerImageFile(null);
      setProfileImagePreview(null);
      setBannerImagePreview(null);
      
      setIsEditAnimating(false);
      setTimeout(() => {
        setIsEditMode(false);
      }, 300);
      
      toast.success('プロフィールを更新しました');
      
      // プロフィールデータを再取得（サイドバーは開いたまま）
      const refreshResponse = await fetch(`${API_URL}/api/actor-profile/${actorId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (refreshResponse.ok) {
        const updatedProfile = await refreshResponse.json();
        setLocalProfile(updatedProfile);
      }
    } catch (err) {
      console.error('Profile update error:', err);
      toast.error(err.message || 'プロフィールの更新に失敗しました');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-700 z-[9998] ${
          isAnimating ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeProfile}
        onTransitionEnd={handleTransitionEnd}
      />
      
      <div className={`
        fixed top-0 right-0 h-full bg-white shadow-2xl z-[9999]
        w-full md:max-w-xl
        transform transition-all duration-700 ease-in-out
        ${isAnimating ? 'translate-x-0' : 'translate-x-full'}
        flex flex-col overflow-y-auto
      `}>
        {/* バナー画像（ヘッダー含む） */}
        <div className={`sticky top-0 z-30 relative h-48 ${
          profile?.banner_image_url 
            ? '' 
            : actorType === 'dao' 
              ? 'bg-gradient-to-r from-orange-400 via-red-400 to-pink-400' 
              : actorType === 'store'
                ? 'bg-gradient-to-r from-green-400 via-teal-400 to-cyan-400'
                : 'bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400'
        }`}>
          {/* 右上ボタンエリア */}
          <div className="absolute top-3 right-3 z-20 flex gap-2">
            {isOwnProfile && (
              <button 
                onClick={() => setIsEditMode(!isEditMode)} 
                className="bg-black bg-opacity-40 hover:bg-opacity-60 text-white p-2 rounded-full transition-all"
                title={isEditMode ? '編集キャンセル' : 'プロフィール編集'}
              >
                {isEditMode ? <FaTimes size={20} /> : <FaEdit size={20} />}
              </button>
            )}
            <button 
              onClick={closeProfile} 
              className="bg-black bg-opacity-40 hover:bg-opacity-60 text-white p-2 rounded-full transition-all"
            >
              <FaTimes size={20} />
            </button>
          </div>
          {profile?.banner_image_url ? (
              <img 
                src={profile.banner_image_url.startsWith('http') ? profile.banner_image_url : `${API_URL}${profile.banner_image_url}`}
                alt="Banner" 
                className="w-full h-full object-cover"
              />
          ) : (
            <div className="w-full h-full relative overflow-hidden">
              {/* 装飾的なパターン */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
                <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full translate-x-20 -translate-y-20"></div>
                <div className="absolute bottom-0 left-1/4 w-24 h-24 bg-white rounded-full translate-y-12"></div>
                <div className="absolute bottom-0 right-1/3 w-28 h-28 bg-white rounded-full translate-y-14"></div>
              </div>
            </div>
          )}
          
          {/* アバターアイコン（固定表示） */}
          {!profileLoading && currentProfile && (
            <div className="absolute bottom-0 left-4 transform translate-y-1/2 z-40">
              <ActorAvatar
                src={
                  currentProfile.profile_image_url ||
                  currentProfile.profile_image ||
                  currentProfile.iconUrl ||
                  null
                }
                alt={getDisplayName()}
                actorType={actorType}
                actorStatus={currentProfile.actorStatus || 'inactive'}
                size="lg"
                className="border-4 border-white shadow-lg"
              />
            </div>
          )}
        </div>
        
        {/* プロフィール情報エリア（スクロール可能） */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-12">
          {profileLoading || !currentProfile ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (() => {
            const profile = currentProfile;
            return (
            <>
          {/* ユーザー名 */}
          <div className="mb-3">
            <h2 className="text-xl font-bold text-gray-800 mb-1">
              {getDisplayName()}
            </h2>

            <div className="flex items-center gap-2 text-xs">
              {actorType === 'dao' && (
                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                  🌲 DAO
                </span>
              )}
              {actorType === 'store' && (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  🏪 ストア
                </span>
              )}
              {actorType === 'user' && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  🌿 ユーザー
                </span>
              )}
              {(() => {
                return profile.lastActiveAt ? (
                  <span className="text-gray-500">
                    {new Date(profile.lastActiveAt).toLocaleString('ja-JP', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                ) : (
                  <span className="text-gray-400">
                    アクティブ履歴なし
                  </span>
                );
              })()}
            </div>
          </div>


          {profile.regionHierarchy && (
            <div className="mb-3">
              <span className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full">
                📍 {profile.regionHierarchy}
              </span>
            </div>
          )}

          {(profile.bio || profile.description) && (
            <div className="mb-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-1">
                自己紹介
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed bg-gray-50 p-2 rounded-lg">
                {profile.bio || profile.description}
              </p>
            </div>
          )}

          <AffiliationsSection actorId={profileActorId} />

          {profile.badges && profile.badges.length > 0 && (
            <div className="mb-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">バッジ</h4>
              <div className="flex flex-wrap gap-1">
                {profile.badges.map((badge, index) => {
                  const tierStyles = {
                    1: { borderClass: '', bgColor: 'bg-orange-50', shadowEffect: 'shadow-sm', progressColor: 'stroke-orange-500' },
                    2: { borderClass: 'border-2 border-gray-300', bgColor: 'bg-gray-100', shadowEffect: 'shadow-md', progressColor: 'stroke-gray-400' },
                    3: { borderClass: 'border-2 border-yellow-300', bgColor: 'bg-yellow-50', shadowEffect: 'shadow-lg shadow-yellow-200', progressColor: 'stroke-yellow-500' },
                    4: { borderClass: 'border-2 border-cyan-300', bgColor: 'bg-cyan-50', shadowEffect: 'shadow-lg shadow-cyan-200', progressColor: 'stroke-cyan-500' },
                    5: { borderClass: 'border-2 border-purple-300', bgColor: 'bg-purple-50', shadowEffect: 'shadow-xl shadow-purple-200', progressColor: 'stroke-purple-500' },
                    6: { borderClass: 'border-2 border-red-300', bgColor: 'bg-red-50', shadowEffect: 'shadow-xl shadow-red-200', progressColor: 'stroke-red-500' },
                  };
                  
                  const style = tierStyles[badge.badge_tier] || tierStyles[1];
                  const { borderClass, bgColor, shadowEffect, progressColor } = style;
                  
                  const badgeCode = badge.badge_code.split('_')[0].toUpperCase();
                  const pointData = profile.ecosicle_points?.find(p => p.token_type === badgeCode);
                  const progress = pointData?.progress_to_next || 0;
                  const hasNextTier = pointData?.next_tier !== null;
                  
                  const radius = 18;
                  const circumference = 2 * Math.PI * radius;
                  const strokeDashoffset = circumference - (progress / 100) * circumference;
                  
                  const tierNames = { 1: 'ブロンズ', 2: 'シルバー', 3: 'ゴールド', 4: 'プラチナ', 5: 'オリハルコン', 6: 'レジェンド' };
                  const tierNamesEn = { 1: 'BRONZE', 2: 'SILVER', 3: 'GOLD', 4: 'PLATINUM', 5: 'ORICHALCUM', 6: 'LEGEND' };
                  const nextTierName = tierNames[pointData?.next_tier];
                  const currentTierName = tierNamesEn[badge.badge_tier];
                  
                  return (
                    <div
                      key={`${badge.badge_code}-${badge.badge_tier}-${index}`}
                      className="relative group"
                      title={`${badge.badge_name} - ${badge.badge_description}\nマイニングブースト: +${parseFloat(badge.fermentation_boost) || 0}%\n減衰期延長: +${parseInt(badge.decay_extension_days) || 0}日${hasNextTier && pointData ? `\n次の${nextTierName}まで: ${pointData.next_threshold - pointData.total_points}pt` : '\n最高ティア達成！'}`}
                    >
                      <svg className="absolute top-0 left-0 w-10 h-10 -rotate-90">
                        <circle
                          cx="20"
                          cy="20"
                          r={radius}
                          className="stroke-gray-200"
                          strokeWidth="2"
                          fill="none"
                        />
                        {hasNextTier && (
                          <circle
                            cx="20"
                            cy="20"
                            r={radius}
                            className={progressColor}
                            strokeWidth="2"
                            fill="none"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            strokeLinecap="round"
                            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                          />
                        )}
                      </svg>
                      <div className={`w-10 h-10 rounded-full overflow-hidden ${bgColor} ${borderClass} ${shadowEffect} flex items-center justify-center transition-all duration-300 hover:scale-110`}>
                        {badge.badge_icon && (
                          <img 
                            src={badge.badge_icon} 
                            alt={badge.badge_name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      {currentTierName && (
                        <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 text-[8px] font-bold text-gray-600 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1 rounded">
                          {currentTierName}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 収支一覧セクション */}
          <div className="mb-3">
            <button
              onClick={() => setFinancesExpanded(!financesExpanded)}
              className="w-full flex items-center justify-between text-left mb-1"
            >
              <h4 className="text-xs font-semibold text-gray-700">📒 収支一覧</h4>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">
                  入 <span className="text-blue-600">{financeSummary.total_income.toLocaleString()}</span> / 
                  出 <span className="text-red-600">{financeSummary.total_expense.toLocaleString()}</span>
                </span>
                <span className="text-gray-400 text-xs">{financesExpanded ? '▼' : '▶'}</span>
              </div>
            </button>

            {financesExpanded && (
              <div className="space-y-1">
                {/* 期間選択タブ */}
                <div className="mb-1">
                  <PeriodSelector 
                    period={financePeriod} 
                    onPeriodChange={setFinancePeriod} 
                  />
                </div>

                {/* 収支リスト */}
                {finances.length > 0 ? (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {finances.map((finance, index) => (
                      <div
                        key={`${finance.type}-${finance.spending_id || finance.trust_id}-${index}`}
                        className="bg-gray-50 rounded p-1.5 text-xs border border-gray-200"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 mb-0.5">
                              <span className={`px-1 py-0.5 rounded text-xs flex-shrink-0 ${
                                finance.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {finance.type === 'income' ? '入' : '出'}
                              </span>
                              <span className="text-gray-500 text-xs">
                                {new Date(finance.created_at).toLocaleDateString('ja-JP')}
                              </span>
                            </div>
                            <div className={`text-xs font-bold mb-0.5 ${
                              finance.type === 'income' ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {finance.type === 'income' ? '+' : '-'}{parseFloat(finance.amount).toLocaleString()} SH {finance.title}
                            </div>
                            {finance.reason && (
                              <div className="text-gray-600 text-xs pl-2">
                                └─ {finance.reason}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-2 text-gray-500 text-xs">
                    データがありません
                  </div>
                )}
              </div>
            )}
          </div>

          {isOwnProfile && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-2">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-3 py-2 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                  <FaShareAlt className="text-green-600 text-xs" />
                  招待リンク
                </h3>
              </div>
              <div className="p-3">
                {!inviteLink ? (
                  <button
                    onClick={generateInviteLink}
                    className="w-full bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center justify-center gap-1"
                  >
                    <FaGift className="text-xs" />
                    リンク生成
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={inviteLink}
                        readOnly
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs bg-gray-50"
                      />
                      <button
                        onClick={copyInviteLink}
                        className={`px-2 py-1 rounded text-xs transition-colors ${
                          inviteCopySuccess
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {inviteCopySuccess ? <FaCheck /> : <FaCopy />}
                      </button>
                    </div>
                    {inviteCopySuccess && (
                      <p className="text-green-600 text-xs">✅ コピーしました</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
            </>
            );
          })()}
        </div>
        
        {/* 固定フッターボタンエリア */}
        <div className="flex-shrink-0 bg-white border-t p-4 shadow-lg">
          <div className="flex space-x-2">
            {!isOwnProfile && currentUser && (
              <>
                {friendshipStatus === null && (
                  <button
                    onClick={handleFriendRequest}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center px-2 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 text-xs whitespace-nowrap"
                  >
                    <FaUserPlus className="mr-1 flex-shrink-0" />
                    <span className="truncate">{loading ? '送信中' : '友達申請'}</span>
                  </button>
                )}
                {friendshipStatus === 'pending_sent' && (
                  <button
                    onClick={handleCancelFriendRequest}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center px-2 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-xs whitespace-nowrap"
                  >
                    <FaTimes className="mr-1 flex-shrink-0" />
                    <span className="truncate">{loading ? '取消中' : '申請取消'}</span>
                  </button>
                )}
                {friendshipStatus === 'pending_received' && (
                  <>
                    <button
                      onClick={handleAcceptFriend}
                      disabled={loading}
                      className="flex-1 flex items-center justify-center px-2 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-xs whitespace-nowrap"
                    >
                      <FaCheck className="mr-1 flex-shrink-0" />
                      <span className="truncate">承認</span>
                    </button>
                    <button
                      onClick={handleRejectFriend}
                      disabled={loading}
                      className="flex-1 flex items-center justify-center px-2 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-xs whitespace-nowrap"
                    >
                      <FaTimes className="mr-1 flex-shrink-0" />
                      <span className="truncate">拒否</span>
                    </button>
                  </>
                )}
                {friendshipStatus === 'accepted' && (
                  <button
                    disabled
                    className="flex-1 flex items-center justify-center px-2 py-2 bg-green-400 text-white rounded-lg cursor-not-allowed text-xs whitespace-nowrap"
                  >
                    <FaCheck className="mr-1 flex-shrink-0" />
                    <span className="truncate">友達</span>
                  </button>
                )}
              </>
            )}

            {onTrust && !isOwnProfile && (
              <button
                onClick={() => onTrust(profile)}
                className="flex-1 flex items-center justify-center px-2 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs whitespace-nowrap"
              >
                <FaHeart className="mr-1 flex-shrink-0" />
                <span className="truncate">信任</span>
              </button>
            )}

            {!isOwnProfile && (
              <button
                onClick={handleToggleFollow}
                disabled={loading}
                className="flex-1 flex items-center justify-center px-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 text-xs whitespace-nowrap"
              >
                <span className="truncate">{loading ? (isFollowing ? '解除中' : 'フォロー中') : (isFollowing ? 'フォロー解除' : 'フォロー')}</span>
              </button>
            )}

            {onChat && !isOwnProfile && friendshipStatus === 'accepted' && (
              <button
                onClick={() => onChat(profile)}
                className="flex-1 flex items-center justify-center px-2 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs whitespace-nowrap"
              >
                <FaComments className="mr-1 flex-shrink-0" />
                <span className="truncate">チャット</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 編集スライドイン */}
      {isEditMode && (
        <>
          <div 
            className="fixed inset-0 bg-black transition-opacity duration-700 z-[10000]"
            style={{ opacity: isEditAnimating ? 0.5 : 0 }}
            onClick={() => {
              setIsEditAnimating(false);
              setTimeout(() => setIsEditMode(false), 700);
            }}
          />
          
          <div className={`
            fixed top-0 right-0 h-full bg-white shadow-2xl z-[10001]
            w-full md:max-w-xl
            transform transition-all duration-700 ease-in-out
            ${isEditAnimating ? 'translate-x-0' : 'translate-x-full'}
            flex flex-col overflow-y-auto
          `}>
            {/* ヘッダー */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex justify-between items-center">
              <button
                onClick={() => {
                  setIsEditAnimating(false);
                  setTimeout(() => setIsEditMode(false), 700);
                }}
                className="text-white p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                <FaTimes size={20} />
              </button>
              <h3 className="text-lg font-semibold text-white">プロフィール編集</h3>
              <button
                onClick={handleSaveProfile}
                disabled={uploading}
                className="text-white px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <FaSave />
                {uploading ? '保存中...' : '保存'}
              </button>
            </div>

            {/* 編集フォーム */}
            <div className="flex-1 overflow-y-auto">
              {/* バナー + プロフィール画像編集（Twitter風レイアウト） */}
              <div className="relative mb-16">
                {/* バナー画像 */}
                <div className="relative h-32 bg-gray-200">
                  {bannerImagePreview ? (
                    <img 
                      src={bannerImagePreview} 
                      alt="Banner preview" 
                      className="w-full h-full object-cover"
                    />
                  ) : currentProfile?.banner_image_url ? (
                    <img 
                      src={currentProfile.banner_image_url.startsWith('http') ? currentProfile.banner_image_url : `${API_URL}${currentProfile.banner_image_url}`}
                      alt="Current banner" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-r from-blue-400 to-purple-400"></div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 hover:bg-opacity-50 transition-opacity pointer-events-none">
                    <FaCamera className="text-white text-2xl" />
                  </div>
                  {/* バナークリックエリア（プロフィール画像の位置を避ける） */}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, 'banner')}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 7rem 100%, 7rem 80%, 0 80%)' }}
                  />
                </div>

                {/* プロフィール画像（バナーに重ねて表示） */}
                <div className="absolute left-4 -bottom-12 w-24 h-24 z-20">
                  <div className="relative w-24 h-24 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-200">
                    <img
                      src={
                        profileImagePreview 
                          ? profileImagePreview 
                          : (currentProfile?.profile_image_url?.startsWith('http') 
                              ? currentProfile.profile_image_url 
                              : `${API_URL}${currentProfile?.profile_image_url || '/uploads/avatars/default-avatar.png'}`)
                      }
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageSelect(e, 'profile')}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-30"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 hover:bg-opacity-50 transition-opacity pointer-events-none">
                      <FaCamera className="text-white text-xl" />
                    </div>
                  </div>
                </div>
              </div>

              {/* フォーム入力エリア */}
              <div className="p-4 space-y-4">

              {/* 表示名 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  表示名
                </label>
                <input
                  type="text"
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="表示名を入力"
                />
              </div>

              {/* 自己紹介 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  自己紹介
                </label>
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="自己紹介を入力"
                  rows="4"
                />
              </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 画像クロップモーダル */}
      {cropImageSrc && (
        <ImageCropModal
          src={cropImageSrc}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspect={cropImageType === 'profile' ? 1 : 16 / 9}
          circularCrop={cropImageType === 'profile'}
        />
      )}
    </>
  );
};

export default ProfileSidebar;
